import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, FileCheck2, Building2, ShieldCheck, MapPin, Ruler } from "lucide-react";
import { api, formatPrice, asItems, formatApiError, markPropertySold, prefixUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { canMarkSoldStatus } from "@/lib/propertyUi";

const getImageUrl = (urlObj) => {
  if (!urlObj) return "";
  const url = typeof urlObj === "string" ? urlObj : (urlObj.url || urlObj.file_url || urlObj.public_url || "");
  if (!url) return "";
  return prefixUrl(url);
};

export default function OfficeVerification() {
  const [tab, setTab] = useState("pending");
  const [pending, setPending] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [docDialog, setDocDialog] = useState(null); // {pid, docs}
  const [approveDialog, setApproveDialog] = useState(null); // {pid}
  const [rejectDialog, setRejectDialog] = useState(null); // {pid}
  const [soldTarget, setSoldTarget] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [reason, setReason] = useState("");
  const [docRemarks, setDocRemarks] = useState({});
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [bookingsError, setBookingsError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    setBookingsError("");
    try {
      const [propertyResult, bookingResult] = await Promise.allSettled([
        api.get("/v1/office/properties/pending"),
        api.get("/v1/office/bookings"),
      ]);
      if (propertyResult.status === "rejected") throw propertyResult.reason;
      setPending(asItems(propertyResult.value?.data));
      if (bookingResult.status === "fulfilled") {
        setBookings(asItems(bookingResult.value?.data));
      } else {
        setBookings([]);
        setBookingsError(formatApiError(bookingResult.reason) || "Bookings could not be loaded.");
      }
    } catch (e) {
      const message = formatApiError(e);
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openDocs = async (pid) => {
    try {
      const { data } = await api.get(`/v1/properties/${pid}`);
      setDocDialog({ pid, docs: asItems(data?.documents || []) });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };


  const reviewDoc = async (documentId, status) => {
    if (!documentId) { toast.error("Missing document id"); return; }
    setBusy(true);
    try {
      await api.post(`/v1/documents/${documentId}/review`, {
        status,
        remarks: docRemarks[documentId] || (status === "verified" ? "Verified by office" : "Rejected by office"),
      });
      toast.success(status === "verified" ? "Document marked verified" : "Document rejected");
      if (docDialog?.pid) await openDocs(docDialog.pid);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const verifyAllDocs = async () => {
    const docsToVerify = (docDialog?.docs || []).filter(
      (d) => (d.verification_status || d.status) !== "verified"
    );
    if (docsToVerify.length === 0) {
      toast.info("All documents are already verified.");
      return;
    }
    setBusy(true);
    try {
      await Promise.all(
        docsToVerify.map((d) => {
          const did = d.document_id || d.id;
          return api.post(`/v1/documents/${did}/review`, {
            status: "verified",
            remarks: docRemarks[did] || "Verified by office",
          });
        })
      );
      toast.success("All documents marked verified successfully");
      if (docDialog?.pid) await openDocs(docDialog.pid);
    } catch (e) {
      toast.error(formatApiError(e));
      if (docDialog?.pid) await openDocs(docDialog.pid);
    } finally {
      setBusy(false);
    }
  };

  const handleOpenApprove = async (p) => {
    const pid = p.id || p.property_id;
    try {
      const { data } = await api.get(`/v1/properties/${pid}`);
      const docs = asItems(data?.documents || []);
      const unverified = docs.filter((d) => (d.verification_status || d.status || "").toLowerCase() !== "verified");
      if (unverified.length > 0) {
        toast.error("Some documents are not verified. Please verify all documents before approving/publishing.");
        openDocs(pid);
        return;
      }
      setApproveDialog({ pid });
    } catch (e) {
      setApproveDialog({ pid });
    }
  };

  const approve = async () => {
    if (!approveDialog) return;
    setBusy(true);
    try {
      await api.post(`/v1/office/properties/${approveDialog.pid}/approve`, {
        remarks: remarks.trim() || "Approved by office",
      });
      toast.success("Approved → awaiting admin publish");
      setApproveDialog(null); setRemarks("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const reject = async () => {
    if (!rejectDialog) return;
    if (!reason.trim()) { toast.error("Rejection reason required"); return; }
    setBusy(true);
    try {
      await api.post(`/v1/office/properties/${rejectDialog.pid}/reject`, { reason: reason.trim(), remarks: remarks.trim() });
      toast.success("Rejected");
      setRejectDialog(null); setReason(""); setRemarks("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const requestSold = (pid) => setSoldTarget(pid);

  const confirmSold = async () => {
    if (!soldTarget) return;
    setBusy(true);
    try {
      await markPropertySold(soldTarget, "Marked sold by office");
      toast.success("Sold request sent to admin verification");
      setSoldTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

    // Purpose: split queue statuses so submitted listings and pending sold verification have their own office tabs.
      const pendingItems = pending.filter(p => ["PENDING_VERIFICATION", "SUBMITTED"].includes(p.status));
      const verifiedItems = pending.filter(p => p.status === "VERIFIED");
      const soldReviewItems = pending.filter(p => p.status === "PENDING_SOLD_VERIFICATION");

      return (
        <div className="space-y-6" data-testid="office-verification-page">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground mb-1">Office Workspace</p>
              <h1 className="font-display text-3xl font-black tracking-tighter text-primary">Verification Queue</h1>
              <p className="text-muted-foreground text-sm mt-1">First-level verification of property listings and documents.</p>
            </div>
            {/* Animated gradient header icon */}
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/15 to-primary/5 ring-1 ring-primary/10 text-primary shadow-sm shrink-0 animate-float-soft">
              <Building2 className="w-7 h-7"/>
            </span>
          </div>

          {loading ? (
            <div className="rounded-xl border border-border bg-card p-10 text-center text-sm text-muted-foreground" role="status">
              Loading verification queue…
            </div>
          ) : error ? (
            <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-8 text-center" role="alert">
              <p className="font-bold text-foreground">Verification queue could not be loaded</p>
              <p className="mt-2 text-sm text-muted-foreground">{error}</p>
              <Button type="button" onClick={load} className="mt-4">Try again</Button>
            </div>
          ) : (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="h-auto w-full justify-start overflow-x-auto p-1">
              <TabsTrigger value="pending" data-testid="office-tab-pending">Pending ({pendingItems.length})</TabsTrigger>
              <TabsTrigger value="verified" data-testid="office-tab-verified">Awaiting Admin Publish ({verifiedItems.length})</TabsTrigger>
              <TabsTrigger value="sold-review" data-testid="office-tab-sold-review">Sold Review Sent ({soldReviewItems.length})</TabsTrigger>
              <TabsTrigger value="bookings" data-testid="office-tab-bookings">Bookings ({bookings.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="pending" className="mt-6">
              {pendingItems.length === 0 ? (
                <EmptyBox label="No properties awaiting verification."/>
              ) : (
                <div className="space-y-3">
                  {pendingItems.map(p => (
                    <PropRow key={p.property_id} p={p} actions={(
                      <>
                        <Button size="sm" variant="outline" onClick={() => openDocs(p.property_id)} data-testid={`office-docs-${p.property_id}`}>
                          <FileCheck2 className="w-3.5 h-3.5 mr-1.5"/>Docs ({p.documents?.length || 0})
                        </Button>
                        <Button size="sm" onClick={() => { setRemarks(""); handleOpenApprove(p); }} data-testid={`office-approve-${p.property_id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1.5"/>Approve
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => { setRejectDialog({ pid: p.property_id }); setReason(""); setRemarks(""); }} data-testid={`office-reject-${p.property_id}`}>
                          <XCircle className="w-3.5 h-3.5 mr-1.5"/>Reject
                        </Button>
                      </>
                    )}/>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="verified" className="mt-6">
              {verifiedItems.length === 0 ? (
                <EmptyBox label="No properties awaiting final admin publish."/>
              ) : (
                <div className="space-y-3">
                  {verifiedItems.map(p => (
                    <PropRow key={p.property_id} p={p} status="VERIFIED" actions={(
                      canMarkSoldStatus(p.status) ? (
                        <Button size="sm" variant="outline" onClick={() => requestSold(p.property_id)} disabled={busy} data-testid={`office-mark-sold-${p.property_id}`}>
                          Mark Sold
                        </Button>
                      ) : null
                    )}/>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="sold-review" className="mt-6">
              {soldReviewItems.length === 0 ? (
                <EmptyBox label="No sold verification requests sent."/>
              ) : (
                <div className="space-y-3">
                  {soldReviewItems.map(p => (
                    <PropRow key={p.property_id} p={p} status="PENDING_SOLD_VERIFICATION"/>
                  ))}
                </div>
              )}
            </TabsContent>

        <TabsContent value="bookings" className="mt-6">
          {bookingsError ? (
            <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-8 text-center" role="alert">
              <p className="font-bold text-foreground">Bookings could not be loaded</p>
              <p className="mt-1 text-sm text-muted-foreground">{bookingsError}</p>
              <Button type="button" variant="outline" className="mt-4" onClick={load}>Try again</Button>
            </div>
          ) : bookings.length === 0 ? (
            <EmptyBox label="No bookings yet."/>
          ) : (
            <div className="space-y-3">
              {bookings.map(b => (
                <div key={b.booking_id} className="border border-border rounded-xl p-4 bg-card transition-shadow duration-200 ease-out hover:shadow-sm" data-testid={`booking-row-${b.booking_id}`}>
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="font-bold text-primary">{b.buyer_name} — {b.buyer_phone}</p>
                      <p className="text-sm text-muted-foreground">Property: <Link className="underline" to={`/properties/${b.property_id}`}>{b.property_id}</Link></p>
                      <p className="text-xs text-muted-foreground mt-1">Booked on {b.booking_date ? new Date(b.booking_date).toLocaleDateString("en-IN") : "date unavailable"}</p>
                    </div>
                    <div className="sm:text-right">
                      <p className="font-display font-black text-xl text-primary">{formatPrice(b.booking_amount)}</p>
                      <Badge className={b.status === "sold" ? "bg-emerald-600" : b.status === "cancelled" ? "bg-rose-600" : "bg-blue-600"}>{String(b.status || "pending").toUpperCase()}</Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
          )}

      {/* Documents Dialog */}
      <Dialog open={!!docDialog} onOpenChange={(o) => { if (!o) setDocDialog(null); }}>
        <DialogContent className="max-w-2xl" data-testid="office-docs-dialog">
          <DialogHeader className="flex flex-row items-center justify-between gap-4 pb-2 border-b border-border">
            <div>
              <DialogTitle>Property Documents</DialogTitle>
              <DialogDescription>Manually verified land documents (Arch §13)</DialogDescription>
            </div>
            {(docDialog?.docs || []).some(d => (d.verification_status || d.status) !== "verified") && (
              <Button
                type="button"
                onClick={verifyAllDocs}
                disabled={busy}
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold shrink-0 shadow-sm transition-all"
                data-testid="office-docs-verify-all"
              >
                <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Verify All Docs
              </Button>
            )}
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto space-y-2">
            {(docDialog?.docs || []).length === 0 && <p className="text-muted-foreground text-sm">No documents uploaded.</p>}
            {(docDialog?.docs || []).map(d => {
              const did = d.document_id || d.id;
              const status = d.verification_status || d.status || "uploaded";
              const documentUrl = getImageUrl(d.url || d.file_path);
              return (
                <div key={did} className="border border-border p-3 rounded-xl bg-background" data-testid={`office-doc-row-${did}`}>
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{String(d.document_type || "document").replace(/_/g, " ")}</p>
                      <p className="text-sm font-bold text-primary truncate">{d.file_name || d.file_path || d.object_key || "Uploaded document"}</p>
                      <p className="text-xs text-muted-foreground truncate">Hash: {d.file_hash || "pending"}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {documentUrl && (
                        <a
                          href={documentUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="px-2.5 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20 text-[10px] font-semibold transition-all whitespace-nowrap"
                        >
                          View Doc
                        </a>
                      )}
                      <Badge className={status === "verified" ? "bg-emerald-600" : status === "rejected" ? "bg-rose-600" : "bg-amber-500"}>
                        {String(status).replace(/_/g, " ")}
                      </Badge>
                    </div>
                  </div>
                  <Textarea
                    className="mt-3"
                    placeholder="Verification remarks for audit log"
                    value={docRemarks[did] || ""}
                    onChange={(e) => setDocRemarks((p) => ({ ...p, [did]: e.target.value }))}
                    data-testid={`office-doc-remarks-${did}`}
                  />
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => reviewDoc(did, "verified")} disabled={busy} data-testid={`office-doc-verify-${did}`}>
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1.5"/>Verify Document
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => reviewDoc(did, "rejected")} disabled={busy} data-testid={`office-doc-reject-${did}`}>
                      <XCircle className="w-3.5 h-3.5 mr-1.5"/>Reject Document
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={!!approveDialog} onOpenChange={(o) => { if (!o) setApproveDialog(null); }}>
        <DialogContent data-testid="office-approve-dialog">
          <DialogHeader>
            <DialogTitle>Approve property</DialogTitle>
            <DialogDescription>
              Marks the property as VERIFIED. An administrator must complete the final publish step.
            </DialogDescription>
          </DialogHeader>
          <Textarea placeholder="Remarks (optional)" value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="office-approve-remarks"/>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialog(null)}>Cancel</Button>
            <Button onClick={approve} disabled={busy} data-testid="office-approve-confirm">{busy ? "..." : "Confirm"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={!!rejectDialog} onOpenChange={(o) => { if (!o) setRejectDialog(null); }}>
        <DialogContent data-testid="office-reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject property</DialogTitle>
            <DialogDescription>Provide a reason — the owner will be notified.</DialogDescription>
          </DialogHeader>
          <input className="w-full border border-border rounded px-3 py-2 text-sm mb-2" placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="office-reject-reason"/>
          <Textarea placeholder="Additional remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)}/>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog(null)}>Cancel</Button>
            <Button variant="destructive" onClick={reject} disabled={busy} data-testid="office-reject-confirm">{busy ? "..." : "Reject"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!soldTarget} onOpenChange={(open) => !open && setSoldTarget(null)}>
        <DialogContent data-testid="office-sold-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Request sold verification?</DialogTitle>
            <DialogDescription>
              This listing will be removed from public availability while an administrator reviews the sold status.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setSoldTarget(null)}>Keep available</Button>
            <Button type="button" onClick={confirmSold} disabled={busy}>{busy ? "Sending…" : "Send for verification"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PropRow({ p, actions, status }) {
  const ld = p.land_details || {};
  return (
    <div className="border border-border rounded-lg p-4 bg-card" data-testid={`office-prop-row-${p.property_id}`}>
      <div className="flex flex-col md:flex-row gap-4">
        <div className="w-full md:w-48 aspect-[4/3] bg-secondary rounded overflow-hidden flex-shrink-0">
          {p.images?.[0] && <img src={getImageUrl(p.images[0])} alt={p.title} loading="lazy" decoding="async" className="w-full h-full object-cover"/>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <Link to={`/properties/${p.property_id}`} className="font-display text-lg font-bold text-primary hover:underline line-clamp-2">{p.title}</Link>
              <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1"><MapPin className="w-3.5 h-3.5"/>{p.location?.locality}, {p.location?.city}</p>
              <div className="flex flex-wrap gap-2 mt-2 text-xs">
                <span className="font-bold text-primary">{formatPrice(p.price)}</span>
                {ld.area && <span className="flex items-center gap-1 text-muted-foreground"><Ruler className="w-3 h-3"/>{ld.area} {ld.area_unit?.replace("_", " ")}</span>}
                {p.verified_owner && <span className="inline-flex items-center gap-1 text-emerald-700"><ShieldCheck className="w-3 h-3"/>Verified Owner</span>}
                <Badge variant="outline">{status || p.status}</Badge>
              </div>
            </div>
          </div>
          {actions && <div className="flex flex-wrap gap-2 mt-3" data-testid={`office-actions-${p.property_id}`}>{actions}</div>}
        </div>
      </div>
    </div>
  );
}

function EmptyBox({ label }) {
  return (
    <div className="py-16 text-center border border-dashed border-border rounded-lg" data-testid="office-empty">
      <p className="text-muted-foreground">{label}</p>
    </div>
  );
}
